/**
 * OAuth Callback Page
 * Handles OAuth redirect from platforms (Meta, Google Ads, TikTok)
 *
 * This page is loaded when the OAuth provider redirects back after authorization.
 * It extracts the authorization code and state from URL parameters,
 * completes the OAuth flow, and redirects the user back to their original location.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getOAuthManager } from '../lib/oauth';

export const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Completing OAuth authorization...');
  const [details, setDetails] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-mount calling the callback twice.
    // OAuth auth codes are single-use — the second call would fail.
    if (calledRef.current) return;
    calledRef.current = true;

    const handleCallback = async () => {
      try {
        // Extract parameters from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for OAuth errors
        if (error) {
          throw new Error(errorDescription || error);
        }

        // Validate required parameters
        if (!code || !state) {
          throw new Error('Missing required OAuth parameters (code or state)');
        }

        setMessage('Exchanging authorization code for access token...');

        // Complete OAuth flow
        const oauthManager = getOAuthManager();
        const result = await oauthManager.handleCallback(code, state);

        if (!result.success) {
          throw new Error(result.error || 'OAuth callback failed');
        }

        // Success!
        setStatus('success');
        setMessage('Authorization successful!');
        setDetails(
          result.account_name
            ? `Connected to ${result.account_name}`
            : `Connected to ${result.platform}`
        );

        // Get redirect URL (where user was before OAuth)
        const redirectUrl = oauthManager.getRedirectUrl() || '/settings';

        // Redirect after short delay
        setTimeout(() => {
          navigate(redirectUrl, { replace: true });
        }, 2000);
      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage('Authorization failed');
        setDetails(error instanceof Error ? error.message : 'Unknown error occurred');

        // Redirect to settings after delay
        setTimeout(() => {
          navigate('/settings', { replace: true });
        }, 5000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-ground">
      <div className="max-w-md w-full mx-4">
        <div className="bg-card rounded-lg shadow-lg p-8">
          {/* Status Icon */}
          <div className="flex justify-center mb-6">
            {status === 'processing' && (
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-work-dot"></div>
            )}
            {status === 'success' && (
              <div className="rounded-full h-16 w-16 bg-ok-soft flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-ok"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            )}
            {status === 'error' && (
              <div className="rounded-full h-16 w-16 bg-bad-soft flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-bad"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Status Message */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-ink mb-2">{message}</h2>
            {details && <p className="text-ink-2 mb-6">{details}</p>}

            {status === 'processing' && (
              <p className="text-sm text-ink-3">Please wait...</p>
            )}

            {status === 'success' && (
              <p className="text-sm text-ink-3">Redirecting you back...</p>
            )}

            {status === 'error' && (
              <div className="space-y-4">
                <p className="text-sm text-ink-3">
                  You will be redirected to settings shortly.
                </p>
                <button
                  onClick={() => navigate('/settings', { replace: true })}
                  className="
                    px-6 py-2 bg-navy text-white rounded-lg
                    hover:bg-primary transition-colors
                  "
                >
                  Go to Settings Now
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
